import type { ChangeCabDeckElement } from '../../types/changes';

export type CabDeckLayerDirection = 'forward' | 'backward' | 'front' | 'back';

/** Mesma regra de `elementKey` no canvas (sem depender do índice quando há id/clientKey). */
export function resolveElementKey(el: ChangeCabDeckElement, index: number): string {
  return String(el.clientKey ?? el.id ?? `tmp-${index}`);
}

export function compareCabDeckElements(a: ChangeCabDeckElement, b: ChangeCabDeckElement): number {
  return a.sort_order - b.sort_order || (a.id || 0) - (b.id || 0);
}

export function sortCabDeckElements(elements: ChangeCabDeckElement[]): ChangeCabDeckElement[] {
  return [...elements].sort(compareCabDeckElements);
}

/** Reindexa 0..n-1 preservando ordem relativa (camada / z-index lógico). */
export function normalizeElementSortOrders(elements: ChangeCabDeckElement[]): ChangeCabDeckElement[] {
  return sortCabDeckElements(elements).map((el, index) => ({ ...el, sort_order: index }));
}

export function elementLayerIndex(
  elements: ChangeCabDeckElement[],
  key: string | number,
): number | null {
  const sorted = sortCabDeckElements(elements);
  const target = String(key);
  const idx = sorted.findIndex((el, i) => {
    const orig = elements.indexOf(el);
    const k = resolveElementKey(el, orig >= 0 ? orig : i);
    return k === target;
  });
  return idx >= 0 ? idx + 1 : null;
}

export function moveElementLayer(
  elements: ChangeCabDeckElement[],
  key: string | number,
  direction: CabDeckLayerDirection,
): ChangeCabDeckElement[] {
  const sorted = sortCabDeckElements(elements);
  const target = String(key);
  const from = sorted.findIndex((el, i) => resolveElementKey(el, i) === target);
  if (from < 0) return elements;

  let to = from;
  switch (direction) {
    case 'forward':
      to = Math.min(sorted.length - 1, from + 1);
      break;
    case 'backward':
      to = Math.max(0, from - 1);
      break;
    case 'front':
      to = sorted.length - 1;
      break;
    case 'back':
      to = 0;
      break;
    default:
      break;
  }
  if (to === from) return normalizeElementSortOrders(sorted);

  const copy = [...sorted];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  /** Preserva a ordem do array; reordenar por sort_order antigo desfaria o movimento. */
  return copy.map((el, index) => ({ ...el, sort_order: index }));
}
